module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('a', {
    attrs: {
      "id": "p71"
    }
  }), _vm._v(" "), _c('h1', {
    staticClass: "CN"
  }, [_vm._v("9")]), _vm._v(" "), _c('h1', {
    staticClass: "chaptertitle "
  }, [_vm._v("Jesus, a Jew?")]), _vm._v(" "), _c('p', {
    staticClass: "left "
  }, [_vm._v("W"), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("HAT RELIGION WAS JESUS?")])]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("A college professor I know routinely includes this question on a comprehensive quiz he gives to incoming freshmen each year. The pattern of responses is constant. Some students answer “Catholic,” most answer “Christian.” A distinct minority answers “Jewish.” It is easy to condescend to students who do not know that Jesus was a Jew, but in fact there are good reasons to be confused about his religious identity. Some of those reasons have to do with the difficulty of imagining what this extraordinary person’s inner life consisted of, and some with whether our compartmentalized idea of religion is relevant to the question. Part of the difficulty has to do with the rampant ambiguities of “Judaism” itself at the time Jesus lived, and part has to do with Christianity’s long attempt to purge itself of Semitic content.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("The famous “quest for the historical Jesus” that so gripped Protestant scholars in the nineteenth century led both to a new appreciation of Jesus’ ties to his native Jewish milieu and to a new emphasis on what separated Jesus from his Jewishness. Jewish scholars at first welcomed Christian explorations into the Jewishness of Jesus, thinking that, as Susannah Heschel puts it, “the more Jewish Jesus could be shown to have been, the more Christians would respect Judaism.” But that is not what happened. “Christians had a different agenda,” Heschel writes. “For them, the more Jewish Jesus was shown to be, the less original and unique he was. If Jesus had simply preached the ordinary Judaism of his day, the foundation of Christianity as a distinctive and unparalleled religion was shattered . . . As strongly as nineteenth-century Jews tried to show an identity between Jesus and Judaism, Christians tried to demonstrate a difference.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_84_1",
      "id": "fnr_fn_84_1",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("1")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note1"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("That theological debate was skewed by political developments, especially in Germany, where, ludicrous as it may now seem, the image of the \n"), _c('a', {
    attrs: {
      "id": "p72"
    }
  }), _vm._v("Aryan Christ emerged as something to be taken seriously. Under Otto von Bismarck (1815–1898), pan-German nationalism jelled, spawning a unifying racial theory, which led to a purified notion of a German "), _c('em', [_vm._v("volk.")]), _vm._v(" Similar efforts had marked Christian dogma and practice, going back to the early times of the Church, but nineteenth-century nationalism brought a new edge to such discussions. Ideas of racial purity as a component of social identity influenced religious identity, leading to a notion of Christianity stripped of all Semitic influence. As important a figure as the philosopher Johann Fichte (1762–1814), for example, had posited a Jesus who was not Jewish at all, and throughout the century theologians followed suit."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_85_2",
      "id": "fnr_fn_85_2",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("2")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note2"
    }
  }), _vm._v(" This would be one of the ways that German Protestant scholars tilled the soil for Nazi antisemitism, promulgating an idolatry of Aryan racial identity by defining Jesus over against Jewishness, not only religiously but racially. Eventually German Protestant hymnals would be “de-Judaized” by the removal of words like “amen,” “hallelujah,” and “hosanna.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_85_3",
      "id": "fnr_fn_85_3",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("3")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note3"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("In the Christian world, the influence of nineteenth-century German Protestant theology was so dominant that it was felt even within Roman Catholicism, especially in the matter of a historical quest for Jesus that led to his removal from the Jewish milieu. As critics of that “quest” remind me now, the illustrated books used in Catholic schools that I attended as a child had been subtly shaped by visual cues. Jesus, Mary, Joseph, and all their intimates, save one, were portrayed with the racial and sartorial characteristics—blue eyes, light brown flowing hair, graceful robes—of northern Europe, in stark distinction to the pictured Pharisees, Sadducees, and high priests, with their odd headdresses, phylacteries, tasseled prayer shawls, oversized noses, and dark skin. It was as if the residents of the towns of Galilee were of a different racial strain than those of Judea—indeed, in the nineteenth century Jesus commonly came to be referred to as “the Galilean,” or “the Nazarene,” an implicit distancing from “Judea,” the region of the Jews. The only obvious Semite in Jesus’ inner circle, of course, was the one named for that region, Judas. The betrayer functioned in this filtered narrative as the one Jew, and the story forever emphasized his motive as greed.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("The occupations of the fishermen friends of Jesus, like Jesus’ own trade of carpenter—think of those pastel scenes of the boy and his dad in that airy, neatly swept workshop, making cabinets—were emphasized to contrast with the Judas-like moneygrubbers whom Jesus would go to Judea to attack. The nineteenth-century quest for the historical Jesus, in other words, in its effort to get behind the façade of an overly divinized Lord, led \n"), _c('a', {
    attrs: {
      "id": "p73"
    }
  }), _vm._v("to the application of nineteenth-century racial categories and cultural stereotypes to first-century Palestine, a way of making Jesus human without making him Jewish."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_86_4",
      "id": "fnr_fn_86_4",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("4")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note4"
    }
  }), _vm._v(" I have been saying “nineteenth-century,” emphasizing the German Protestant origins of this mindset, but this was all still thoroughly in place in the crucifix and stained glass of St. Mary’s Roman Catholic Church in Alexandria, Virginia, and in the textbooks and bulletin-board posters of my parochial school, by which, despite myself, I continue to measure God."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_86_5",
      "id": "fnr_fn_86_5",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("5")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note5"
    }
  }), _vm._v(" One would think that six years of Scripture study and theology in a rigorous seminary at the time of the revolutionary Vatican II would have remedied this shallow notion of who Jesus was, but German Protestant theology and scholarship, still largely uncriticized for its implicit anti-Judaism, was in the early 1960s more influential in Catholic circles than ever.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("True, the most patently childish notions—that cabinetmaker’s workshop, Jesus hand-carving birds, then bringing them to life—had dropped away. But an idea that distanced Jesus even further from Jewishness had taken over my understanding. I learned to think of Jesus as a mystical genius whose direct experience of God the Father, whom he called Abba (“Daddy”), was such that he had no need of any mediating culture. Religion is by definition such a culture. Here is how one of the theologians I learned this from, Bernard Cooke, explains it: “What was distinctive about Jesus’ experience [of God] was its intimacy and immediacy. All the textual evidence points to the fact that Jesus’ knowledge of his Abba was immediate personal acquaintance.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_86_6",
      "id": "fnr_fn_86_6",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("6")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note6"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("The word “religion” shares a root with “ligament,” meaning “tie.” Religion exists to overcome the gulf between creatures and Creator. It is a system of beliefs and rituals that ties the human to God. But Jesus was presented, in this understanding, as the one man who had no need of such a tie. “Believe me that I am in the Father and the Father is in me,” the Gospel of John reports him as saying."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_86_7",
      "id": "fnr_fn_86_7",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("7")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note7"
    }
  }), _vm._v(" The theology that develops from that mystical union makes Jesus himself the ligament. So the question of the religious identity of Jesus never arises—not Jewish religion, not Christian religion—because his knowledge of God is immediate. He has no need of the ligament of religion. If he at first participated in Jewish ritual, he did so for the sake of form, not because he needed it. And the Gospels show him distancing himself from Jewish religious observances. As Paula Fredriksen points out, for example, the Gospel of Mark shows Jesus dismissing central religious traditions of Judaism like “Shabbat, food, tithing, Temple offerings, purity—as the ‘traditions of men.’ To these \n"), _c('a', {
    attrs: {
      "id": "p74"
    }
  }), _vm._v("he opposes what Jesus ostensibly propounds as ‘the commandment of God’ (7:8). The strong rhetoric masks the fact that these laws are biblical and, as such, the common concern of all religious Jews: It is God in the Torah, not the Pharisees in their interpretations of it, who commanded these observances.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_87_8",
      "id": "fnr_fn_87_8",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("8")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note8"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("When the disciples of Jesus asked him how to pray—this story became the core of my belief in him—he replied with the Our Father."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_87_9",
      "id": "fnr_fn_87_9",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("9")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note9"
    }
  }), _vm._v(" Christians recite this prayer in rote fashion, as if it were the farthest thing from religiously revolutionary, when in fact it is nothing less than an invitation to call God “Daddy”—that is, to think of the Almighty One, the Ineffable, in the most intimate way. Ironically, this aspect of Jesus’ spirituality, which for most Christians has had the effect of distancing him from Judaism, actually shows him participating in its vital and at that time multifaceted manifestation. As the Catholic scholar John Pawlikowski has written, “In particular, Jesus’ stress on his intimate link with the Father picks up on a central feature of Pharisaic thought.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_87_10",
      "id": "fnr_fn_87_10",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("10")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note10"
    }
  }), _vm._v(" Indeed, there is evidence that, by the time of Jesus, Jews were regularly praying to God as Father."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_87_11",
      "id": "fnr_fn_87_11",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("11")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note11"
    }
  }), _vm._v(" But that was never explained to us. The intimacy Jesus claimed to have with God the Father was made to seem unique, entirely his. More than anything else, to us, it set him apart from Jews.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("Based on what was presented to us, we could only have concluded that, if anything, Jesus’ Abba experience put him at odds with Jewish religion, for, as Cooke puts it, “There were fundamental incongruities between the Abba he experienced and the God known and explained by those around him.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_87_12",
      "id": "fnr_fn_87_12",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("12")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note12"
    }
  }), _vm._v(" This spirituality had the simple effect of deleting any reference to Jewish cult in the life of Jesus. It was impossible to picture him in that tasseled prayer shawl, wearing phylacteries, entering the Temple not to protest but to pray. Having learned in parochial school that Jesus was racially not Jewish, I learned in graduate theological school that he was religiously not Jewish either. Susannah Heschel characterizes the Aryanizing of Jesus as an effort “to create a "), _c('em', [_vm._v("judenrein")]), _vm._v(" Christianity for a "), _c('em', [_vm._v("judenrein")]), _vm._v(" Germany,”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_87_13",
      "id": "fnr_fn_87_13",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("13")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note13"
    }
  }), _vm._v(" but this spiritualizing of Jesus was a "), _c('em', [_vm._v("judenrein")]), _vm._v(" of such subtlety that I did not know, until reflecting on Heschel’s recent work, that it had completely dominated my religious imagination."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_87_14",
      "id": "fnr_fn_87_14",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("14")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note14"
    }
  }), _vm._v(" What religion was Jesus? I’d have surely answered Jewish, unlike those ill-informed college freshmen—but their answers were more honest than mine.")]), _vm._v(" "), _c('div', {
    staticClass: "timebreak "
  }), _vm._v(" "), _c('p', {
    staticClass: "left "
  }, [_vm._v("What is a Jew anyway? At the end of the second millennium, Jews themselves carry on the argument, with the ultra-Orthodox of Mea Shearim, \n"), _c('a', {
    attrs: {
      "id": "p75"
    }
  }), _vm._v("their enclave in Jerusalem, aiming anathemas at the secular children of David Ben Gurion, modern Israel’s first prime minister. Hitler said that a Jew was anyone who had at least one Jewish grandparent, and, as if to spite him, many Jews adopted that definition. The rabbis, holding to matrilineal descent, define a Jew as someone having a Jewish mother. In the state of Israel, a Jew can be an atheist, although not a baptized Christian. Part racial, part religious, the meaning of Jewishness today is ambiguous. In his memoir, the drama critic Richard Gilman described a life’s journey that had taken him from the Jewish faith into which he was born, into unbelief, then into Roman Catholicism, from which he subsequently “lapsed.” And where did that leave him? As “a lapsed Jewish-atheist-Catholic. Fallen from all three, a triple deserter!” But not quite. In the end, he had, without choosing it, resumed his original identity. “The difference is that you stay Jewish in your bones and pores, there’s no lapsing from that; changed names or nose bobs won’t do.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_88_15",
      "id": "fnr_fn_88_15",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("15")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note15"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("The contemporary argument among ultra-Orthodox Jews, Reform Jews, and secular Jews over the question Who is a Jew? points to a piece of the social and political context that is mainly missing from the Christian memory of foundational events. To imagine that first Jesus and then his followers were in conflict with “the Jews,” a conflict with the sequential climaxes that occurred when “the Jews” killed Jesus and then certain of his followers, is, of course, to ignore the fact that Jesus and his first followers were themselves Jews. But on a more basic level, it is to assume that there was a social-religious entity called “the Jews.” Obviously, a period of time had to pass before something called “Christianity” came into being as a distinct community, but emphasis on that evolution ignores the fact that, in the same period, there was no clearly defined “Judaism” either. Indeed, the suffix “ism,” suggesting a set of coherent ideological boundaries, a membership definition, a precisely notated theology and cult, is anachronistic. If my great-uncle’s story was misremembered by my family, it was because the post-1916 Irish imagination could no longer contain the ambiguous experience of a dual loyalty to London and Dublin. If the story of Jesus is misremembered, with devastating effect on the Jews, however defined, it is first because a later Christianity presumed a univocal—and, not incidentally, flawed—Judaism against which to define its uniqueness and value. But there was no such Judaism.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("“When Jesus was born,” the Columbia University scholar Alan Segal writes, “the Jewish religion was beginning a new transformation, the rabbinic movement, which would permit the Jewish people to survive \n"), _c('a', {
    attrs: {
      "id": "p76"
    }
  }), _vm._v("the next two millennia. The complex of historical and social forces that molded rabbinic Judaism also affected the teachings of Jesus, helping to form Christianity into a new and separate religion.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_89_16",
      "id": "fnr_fn_89_16",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("16")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note16"
    }
  }), _vm._v(" Segal entitled his book "), _c('em', [_vm._v("Rebecca’s Children,")]), _vm._v(" reflecting a theme already noted, that it is useful to think of the two religions as siblings, which, like Jacob and Esau, struggled against each other even in their mother’s womb. The history of the origins of Jewish-Christian conflict suggests that the metaphor of rivalrous fraternity is more than a metaphor; it actually defines the way these two religions came into being. In Jesus’ lifetime and shortly after it, Segal writes, “Dislocation, war, and foreign rule forced every variety of Jewish community to rebuild its ancient national culture into something almost unprecedented, a religion of personal and communal piety. Many avenues were available to Jews for achieving this new sense of personal piety, one of which was Jesus’ movement.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_89_17",
      "id": "fnr_fn_89_17",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("17")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note17"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("When a Christian asks Who is a Jew? he risks falling into the trap of a mythic projection of perennial Christian anxiety, defining Jewishness in a way that serves a Christian purpose. Obviously, Judaism defines itself in its own terms. In trying to understand the origins of Jewish-Christian conflict, perhaps it would be more useful to put the question as those first rivals within the broad Jewish community might have, which would be to ask, in effect, Who is the “true Israel”?"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_89_18",
      "id": "fnr_fn_89_18",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("18")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note18"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("Competing answers were offered by the groups characterized in the New Testament. There were the Pharisees, whose movement evolved into rabbinic Judaism, referred to earlier. Some Pharisees were priests, although most were laymen, and their religious impulse, competing with Temple sacrifice, emphasized the study of Torah in their synagogues and the rigorous keeping of the Law. Josephus says that six thousand Jews were Pharisees."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_89_19",
      "id": "fnr_fn_89_19",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("19")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note19"
    }
  }), _vm._v(" There were Sadducees, whom we might recognize as aristocrats, and some of them were high priests whose religious focus was the sacrificial cult of the Jerusalem Temple. They were inclined to cooperate with the Roman occupiers."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_89_20",
      "id": "fnr_fn_89_20",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("20")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note20"
    }
  }), _vm._v(" It is not clear how large this party was, but according to the distinguished scholar E. P. Sanders, there were many thousands of priests."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_89_21",
      "id": "fnr_fn_89_21",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("21")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note21"
    }
  }), _vm._v(" Josephus argues that the majority of Jews would have inclined toward such cooperation with Rome."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_89_22",
      "id": "fnr_fn_89_22",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("22")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note22"
    }
  }), _vm._v(" The Sadducees, in effect, formed a core of the establishment. There were Essenes, famous now for their caves in Qumran,"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_89_23",
      "id": "fnr_fn_89_23",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("23")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note23"
    }
  }), _vm._v(" but in the first century they were a countersect that rejected the corruptions of the cities, and in particular of Herod’s Temple, which to them was a Hellenized blasphemy. Herod the Great (c. 73–4 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C.E.")]), _vm._v("), the half-Jewish Roman puppet, had ruled as king of \n"), _c('a', {
    attrs: {
      "id": "p77"
    }
  }), _vm._v("Israel, including Judea and Galilee, since 37 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C.E.")]), _vm._v(" He is remembered by Christians for the story of his slaughter of the innocents at the time of Jesus’ birth, but his greatest undertaking was the restoration of the Temple in Jerusalem, which for him fulfilled a political purpose as much as a religious one. The Temple was designed to impress his Roman overlords as much as his Jewish subjects. But because of that duality, the Temple was a flashpoint to the Essenes, who wanted to replace the Romans as rulers of Israel with their own leaders. Josephus put their number at more than four thousand."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_90_24",
      "id": "fnr_fn_90_24",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("24")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note24"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("The numbers offered by Josephus, while not to be taken as precise, indicate that relatively few Jews belonged to the identifiable parties. But the broader population would have had clear sympathies one way or another. At one extreme—in our terms, perhaps, the “liberals”—would have been the Hellenizers, those open to the customs of the Gentiles. By and large, these Jews would have been of the Diaspora—Greek speakers, men and women who had learned to live within and take for granted the pagan culture of Greek and Roman cities. Most famous of these would have been Philo of Alexandria (c. 30 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C.E")]), _vm._v("–45 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("C.E.")]), _vm._v("), who wrote favorably, for example, of the emperor Augustus. But many Palestinian Jews would probably have rejected Hellenization, and that is especially true of the rural people, whose experience of the wider world would have been limited.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("At the other extreme from “liberals” would have been “zealots,” whether pacifists or violent revolutionaries—pietists or apocalyptic believers who looked for divine intervention as a means of restoring Israel. An example of such a movement, perhaps, would have been that of John the Baptist. He was a radical spiritualist, yet his direct challenge to Herod, for which he was beheaded, demonstrates the impossibility of separating religion from politics in this milieu. In addition to the main parties and the sects, there were powerful regional divisions among those who identified themselves as the “true Israel.” Judeans were dominant because the cultic center was in Jerusalem, yet there were Samaritans who, worshiping at their own Mount Gerizim instead of on Mount Zion, were disdained by Judeans."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_90_25",
      "id": "fnr_fn_90_25",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("25")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note25"
    }
  }), _vm._v(" And there were the villagers of Galilee, whom city-dwelling Judeans would have looked down upon as peasants. In turn, Galileans would have regarded the Jewish oligarchs of Jerusalem both as near traitors for accommodating Rome and as idolaters for allowing images of Caesar to be venerated, if only on coins. (Jesus’ question about the coin, “Whose likeness and inscription is this?,”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_90_26",
      "id": "fnr_fn_90_26",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("26")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note26"
    }
  }), _vm._v(" is a sly jibe at his challengers’ idolatry.)")]), _vm._v(" "), _c('a', {
    attrs: {
      "id": "p78"
    }
  }), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("The true Israel. Centered in the Temple. The Torah. The oneness of God. The idea of election. The covenant. Each of these metaphors had adherents who gave it priority. But it is important to recall the rather obvious fact that such debate, in Fredriksen’s phrase, “coexisted with consensus.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_91_27",
      "id": "fnr_fn_91_27",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("27")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note27"
    }
  }), _vm._v(" Indeed, agreement on those elements as essential, however much one or the other was emphasized, would have been the precondition of diversity within the community. Still, it is impossible to detect in the vibrant religious expressions of first-century Palestine an all-encompassing Jewish orthodoxy. The very sectarianism of Israel in the time of Jesus appears to be its defining note. Segal argues that sectarian multiplicity amounted to an efficient channeling of conflict among classes and across ideologies, achieving a remarkable social balance in an era of massive cultural mutation."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_91_28",
      "id": "fnr_fn_91_28",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("28")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note28"
    }
  }), _vm._v(" That is why Jesus was acting exactly like a Jew of his time when, apparently influenced by John the Baptist, he initiated yet another sectarian movement, and like a Jew of his place—Galilee—when he targeted the Herod-compromised Temple in Jerusalem as the site of his defining spiritual-political act.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("So the college students who didn’t label Jesus as a Jew inadvertently score a point, because Jesus, while taking his membership in the covenant people Israel for granted, would likely have thought of himself not as a Judean (from which our word “Jew” derives, but which to him would have implied geography) but as a Galilean. “The Nazarene” after all. What was it to be a first-century Galilean? In trying to imagine Jesus’ experience—and in trying to understand how his became a story told against Judaism instead of within Judaism—there is a key element yet to be considered. It is the most important element, yet it is also one often left aside. To tell the story of Christian origins (or the origins, for that matter, of rabbinic Judaism) without reference to it is equivalent to telling the story of the 1916 Irish Rising without reference to the Great War. And war—war every bit as savage, in relative terms, as World War I—is the missing element.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("War was not in any way missing from Palestine when Jesus was born. Nor was war missing from the direct experience of his followers, his followers’ children, of their children, and of their great-nephews. The origins of the Jesus movement, and ultimately of Christianity, cannot be understood apart from the century-long Roman war against the Jews, albeit a war punctuated by repeated acts of Jewish rebellion. That is the social and political context that is all too often missing from the memory: Jesus and his movement were born in the shadow of what would stand as the most grievous violence against the Jewish people until Hitler’s attempt at a Final Solution."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_91_29",
      "id": "fnr_fn_91_29",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("29")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note29"
    }
  }), _vm._v(" (I would add here that it is equally misguided to consider \n"), _c('a', {
    attrs: {
      "id": "p79"
    }
  }), _vm._v("the late-twentieth-century ferment in Christian theology, symbolized by the Second Vatican Council, apart from the trauma of the Holocaust, including the failure of the Christian churches to resist it.)")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("Between half a million and a million Jews lived in Palestine at the time of Jesus’ birth."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_92_30",
      "id": "fnr_fn_92_30",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("30")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note30"
    }
  }), _vm._v(" Some scholars put the Jewish population there as high as two and a half million, with a few hundred thousand Gentiles. Sanders accepts a figure of “less than a million, possibly only about half that.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_92_31",
      "id": "fnr_fn_92_31",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("31")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note31"
    }
  }), _vm._v(" Later we will see that Josephus posits Jewish casualty figures in the war with Rome that Sanders finds too high. Whatever the totals, the ratio of Jewish dead in Palestine at the hands of Rome may well approximate the twentieth-century record of one in three. Already, when Jesus was born, the inhabitants of his region were a defeated, violated people. The brutally effective Roman general Pompey (106–48 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C.E.")]), _vm._v("), undertaking a major clampdown on the Asian provinces of the empire, had set his legions loose throughout the area little more than half a century before (63 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C.E.")]), _vm._v(")."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_92_32",
      "id": "fnr_fn_92_32",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("32")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note32"
    }
  }), _vm._v(" He conquered Jerusalem. Thus began a period of oppressive colonial occupation"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_92_33",
      "id": "fnr_fn_92_33",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("33")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note33"
    }
  }), _vm._v(" that would climax twice: when Roman garrisons leveled Jerusalem in 70 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("C.E.,")]), _vm._v(" and again—once and for all—in 135 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("C.E.")])]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("Largely because we are heirs to a Roman imperial culture that controlled the writing of history, we are inclined to read Rome’s story through rose-colored lenses. We tend to see the march of the Roman Empire as a civilizing work of human progress. Every schoolchild knows that the darkness of barbarianism was penetrated by Julius Caesar, who brought order to its chaos. “All Gaul,” we learned, “is divided into three parts.” But we never asked who was doing the dividing, or how the dividees felt about it."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_92_34",
      "id": "fnr_fn_92_34",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("34")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note34"
    }
  }), _vm._v(" We accepted the idea of a system according to which only citizens had rights, and roles in the story. Saint Paul’s story, for example, takes a dramatic turn when, as Acts tells it, he announces his citizenship."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_92_35",
      "id": "fnr_fn_92_35",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("35")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note35"
    }
  }), _vm._v(" Only then are the Romans who arrested him bound by what we call due process. In the story of Rome, all others, especially that invisible mass of slaves, are the forever unnamed—and forever unentitled to any semblance of due process. We mark Rome’s progression from a republic to a dictatorship, and while we take note of the madness of a Caligula (12–41 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("C.E.")]), _vm._v("), who had himself worshiped as a god, or a Nero (37–68 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("C.E.")]), _vm._v("), who killed himself saying “What an artist I perish,” reports of their brutality serve mainly to emphasize the relative worthiness of most rulers. We are conditioned to think of the decline and fall of Rome sentimentally, as tragedy pure and simple. The gradual dissipation of imperial power, leading to vulnerability before the northern hordes, is the condition only of a new darkness.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("But what if Roman imperial power itself, not in decline but at the peak, \n"), _c('a', {
    attrs: {
      "id": "p80"
    }
  }), _vm._v("was the real darkness? A British critic and author of several important works on early Christianity, A. N. Wilson, says that Rome “was the first totalitarian state in history,” the first to extend absolute control over the lives of a vast population. When compared to other empires of antiquity, Rome comes off well in some ways. The Greeks under Alexander, for example, imposed their language on those they conquered, while the Romans allowed local languages and cultures to remain intact. That is why Greek was the lingua franca of the Hellenized world. In addition, the breadth of religious diversity in Rome itself shows that the caesars tolerated, and even admitted to the pantheon, local gods. But the Roman war machine, once set running, was ruthless beyond what the world had seen. And though local gods were left alone, Rome was perhaps the first empire to require of its subjects an at least outward show of assent to the proposition that the emperor, too, was God."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_93_36",
      "id": "fnr_fn_93_36",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("36")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note36"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("It is the glories of Roman dominance that are emphasized in the cultural memory of Western civilization—those arrow-straight roads, elegant aqueducts, timeless laws, conjugated language—to the exclusion of what the imposition of those glories cost those on whom they were imposed. What if, when we thought of Caesar, we thought less of Cleopatra’s lover or Virgil’s patron or Marcus Aurelius’s delicate conscience than, say, of a Joseph Stalin or a Pol Pot whose program worked? How would history tell the story of the twentieth century if it were the first century of the thousand-year Reich? It all depends on where you stand. It may be anachronistic to judge the policies of a great empire of antiquity by the standards of the U.N. Declaration of Human Rights, but if being human means anything, it is that a minimal level of decent treatment is required in every culture and era."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_93_37",
      "id": "fnr_fn_93_37",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("37")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note37"
    }
  }), _vm._v(" It is clear that from the point of view of those on the bottom of the Roman pyramid—indeed, under it—that such a minimal standard was not met.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("To the peasant peoples of the Roman-dominated world, to the millions of slaves and petty laborers (in Rome itself, fully one million of the population of two million were slaves"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_93_38",
      "id": "fnr_fn_93_38",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("38")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note38"
    }
  }), _vm._v("), to the lepers and beggars, to the troublemakers whose lives could be snuffed out with little notice taken, no characterization of Caesar’s evil would have been too extreme. We have looked back at Rome from above—from the point of view, that is, of those who benefited from its systems, traveled its roads, beheld its architectural wonders, learned to think in its language—but what of that vast majority who drew no such benefit? There is no understanding either the Jesus movement itself or the foundational memory of its violent conflict \n"), _c('a', {
    attrs: {
      "id": "p81"
    }
  }), _vm._v("with the Jews if we cannot look back from below, from the vantage of those for whom the Roman systems were an endless, ever-present horror. It was to them, above all, that the message of Jesus came to seem addressed.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("Most of the subjugated peoples in the Mediterranean world yielded to the Romans in what Romans regarded as essential, and those who refused to do this found themselves required to yield in everything, surrendering whatever was distinctive in their cultural identities to the dominant occupier. That is why we know so little of the Phoenicians, say, or the Nabataeans. The people in Palestine proved to be especially stubborn, clinging doggedly, and despite efforts at coercion and co-optation, to a self-understanding that permanently set them apart. But Jewish resistance arose from something far deeper than some pseudogenetic stiff-necked stubbornness that would one day inspire an antisemitic stereotype. For the Jews of Palestine, the indignity of an emperor-worshiping colonizer’s foot on the throat was compounded by the religious convictions that no such emperor was divine and, more pointedly, that their freedom in this now violated land was a gift from the one true God—their God. Despite everything that set them apart, the rivalrous groups of Jews agreed that the land was a sacred symbol of that God’s enduring promise. So for Palestinian Jews of all stripes, the Roman occupation as such was a religious affront as well as a political one. Furthermore, and equally across the board, a Jew’s belief in the covenant included the belief that, one way or another, sooner or later, God would fulfill the promise again, as God had done repeatedly in history. God would do this once the purpose of this humiliating defeat—some, like John the Baptist, said its purpose was to bring the people to repentance—was fulfilled. God would do it by vanquishing the foreign invader and restoring to Israel its holy freedom. In other words, Jews "), _c('em', [_vm._v("as Jews")]), _vm._v(" had a reason to resist Rome, and a reason to believe, despite Rome’s overwhelming military superiority, that the resistance would be effective.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("What Jews did not have was anything approaching agreement on the form this resistance should take. And it is here that the other, negative meaning of Jewish sectarianism surfaces. Typically, imperial powers depend on the inability of oppressed local populations to muster a unified resistance, and the most successful occupiers are skilled at exploiting the differences among the occupied. Certainly that was the story of the British Empire’s success, and its legacy of nurtured local hatreds can be seen wherever the Union Jack flew, from Muslim-Hindu hatred in Pakistan \n"), _c('a', {
    attrs: {
      "id": "p82"
    }
  }), _vm._v("and India, to Catholic-Protestant hatred in Ireland, to, yes, Jew-Arab hatred in modern Israel."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_95_39",
      "id": "fnr_fn_95_39",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("39")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note39"
    }
  }), _vm._v(" Rome was as good at encouraging internecine resentments among the occupied as Britain ever was. At one level, it is a matter only of exploiting the temperamental differences that perennially divide conservatives, moderates, and radicals from one another. E. P. Sanders says that for Jews confronted with “the great empires of the Mediterranean,” the various parties had to decide “when to fight, when to yield; when to be content with partial independence, when to seek more. In terms of internal affairs, the primary issue was who would control the national institutions: the temple, the sacrifices, the tithes and other offerings, and the administration of the law.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_95_40",
      "id": "fnr_fn_95_40",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("40")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note40"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("Sectarian conflict amounted to more than mere squabbling. There were grave tensions involving the life and death of the nation of Israel, and every aspect of its existence could be disputed because Israel’s God had become involved at every level. Today, even believers take for granted the “wall of separation,” in Jefferson’s phrase, between areas of God’s concern and those of government’s, but it was not so at the time of Jesus. “There was no simple distinction,” Sanders says, “between ‘church’ and ‘state’ or ‘religion’ and ‘politics.’ God, in the eyes of Jews, cared about all aspects of life; no part of it was outside ‘religion.’ Thus, in any case in which there was a choice—whether between would-be rulers, competing architectural plans for the temple, or various prohibitions on the sabbath—Jews would attempt to discern and follow "), _c('em', [_vm._v("God’s")]), _vm._v(" will. Not infrequently they disagreed.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_95_41",
      "id": "fnr_fn_95_41",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("41")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note41"
    }
  }), _vm._v(" In every case, their disagreement served the purposes of Rome.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("To the radical revolutionaries who wanted to mount an immediate, violent assault on the occupier, the impulse of aristocrats to cut the best deal with the enemy looked like collaboration or treason; equally, from inside the Temple precincts, the radicals’ fanaticism looked like suicide. So the establishment party of Sadducees, associated with the priestly class, participated from their place at the Temple in the administration of Roman power in Jerusalem; the separatist Zealots, like the monastics at Qumran, pursued a rejectionist path; the Pharisees advocated an adherence to Mosaic law as a way of ushering in God’s liberating intervention; and the Sicarii launched knife-wielding terrorist attacks against agents of the occupiers. What the Romans could depend on—a classic exercise of divide-and-keep-conquered—was each group’s readiness to identify a competing group as the primary enemy, often leaving Rome above the fray. For our purposes, the point is that even in the way events of this era are remembered, the unleashed sectarian impulse continued to keep the Roman overlords at the margin of the story.")]), _vm._v(" "), _c('a', {
    attrs: {
      "id": "p83"
    }
  }), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("Take two examples, one from the beginning and one from the end of the story of Jesus, as his followers told it to each other and the world. First, in the year 4 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C.E.,")]), _vm._v(" which also happened to be the year of Jesus’ birth,"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_96_42",
      "id": "fnr_fn_96_42",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("42")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note42"
    }
  }), _vm._v(" Herod the Great died. His death left a temporary power vacuum, which caused violent outbreaks among forces loyal to various pretenders to succeed Herod as Rome’s client king and among the followers of messianic movements who sought to seize an opening against Rome."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_96_43",
      "id": "fnr_fn_96_43",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("43")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note43"
    }
  }), _vm._v(" The Romans smashed every rebellion and, with those legions pouncing from Syria, restored direct imperial rule. As summed up by the scholars Richard Horsley and Neil Asher Silberman: “The Roman armies had swept through many of the towns and villages of the country, raping, killing, and destroying nearly everything in sight. In Galilee, all centers of rebellion were brutally suppressed; the rebel-held town of Sepphoris was burned to the ground, and all its surviving inhabitants were sold into slavery.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_96_44",
      "id": "fnr_fn_96_44",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("44")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note44"
    }
  }), _vm._v(" Thousands of Jews were killed. Villages in Galilee were laid waste. In Jerusalem, where rebels had briefly taken charge, the Romans showed the lengths to which they were prepared to go to maintain control by swiftly executing anyone even suspected of collusion in the rebellion—Josephus puts the number at two thousand."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_96_45",
      "id": "fnr_fn_96_45",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("45")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note45"
    }
  }), _vm._v(" The Roman means of execution, of course, was crucifixion, and Josephus makes the point that indeed the victims were crucified. This means that just outside the wall of the Jewish capital, crosses were erected—not three lonely crosses on a hill, as in the tidy Christian imagination, but perhaps two thousand in close proximity. On each was hung a Jew, and each Jew was left to die over several days the slow death of suffocation, as muscles gave out so that the victim could no longer hold himself erect enough to catch a breath. And once squeezed free of life, the corpses were left on their crosses to be eaten by buzzards. This grotesquery was its own justification. Its power was magnified because for Jews, coming into contact with a corpse made one ritually impure—a priest, for example, could not bury a parent. Such impurity could even be acquired by “overshadowing” a corpse, or being “overshadowed” by one."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_96_46",
      "id": "fnr_fn_96_46",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("46")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note46"
    }
  }), _vm._v(" The shadows of those crucifixes, in other words, were also the point. The Jews who’d been left alive were being reminded whom they were dealing with in Rome, reminded for weeks by the sight and stench of the bodies. The image of those scores of crosses would stamp Jewish consciousness for a generation."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_96_47",
      "id": "fnr_fn_96_47",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("47")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note47"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("The opening chapters of the Gospel of Matthew evoke the political and social stresses of the world into which Jesus was born, but doesn’t it seem odd that the ruthlessness displayed in Matthew’s account of the slaughter of the innocents—the murder of every male child under two in the town \n"), _c('a', {
    attrs: {
      "id": "p84"
    }
  }), _vm._v("of Bethlehem, a very few miles from those crosses—belonged not to the Romans but to the Jewish king Herod? This is not to dismiss that crime, if it occurred, nor to deny Herod’s brutality, especially in the madness of his last years, but only to note that in the Christian memory—the Gospel of Matthew, usually dated to the decade of the 80s "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("C.E.,")]), _vm._v(" was written long after these events took place—the Roman crime is forgotten while the Jewish one is highlighted. Similarly with the Gospel of Luke, which was composed about the same time as Matthew. Luke’s nativity-narrative reference to Caesar Augustus (63 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C.E")]), _vm._v("–14 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("C.E.")]), _vm._v(") as issuing a decree “that all the world should be enrolled,”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_97_48",
      "id": "fnr_fn_97_48",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("48")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note48"
    }
  }), _vm._v(" which moved the action of the Mary and Joseph story to Bethlehem in the first place, cries out for elaboration. It was the same Caesar Augustus who declared himself “Savior of the world,” making him anathema to Jews. When he came to power with the Senate’s authority in 27 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("B.C.E.,")]), _vm._v(" it was as the head of a republic, but when he died in 14 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("C.E.,")]), _vm._v(" it was as the emperor of a dictatorship, one tool of which was that world census. The perfect symbol of Caesar’s regime was the gibbet on which those who refused to be part of his all-encompassing blasphemy were hung to die.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("Now the second example, from the end of Jesus’ life. When that Roman gibbet finally enters his story, by an extraordinary set of narrative machinations it is hardly Roman at all. Certainly the Gospel accounts are explicit in describing the Romans as the executioners of Jesus, but if they are coconspirators with the Jewish high priests and leaders of the Jewish ruling body, the Sanhedrin, they are decidedly "), _c('em', [_vm._v("unindicted")]), _vm._v(" coconspirators, which in modern law is a distinction between parties to a crime and perpetrators of it. According to the Christian memory, as conjured again by Matthew, the hand of the hand-washing Pilate (whose term as procurator, or appointed governor, in Judea ran from 26 to 36 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("C.E.")]), _vm._v(") is forced by the bloodthirstiness of the crowd. “I am innocent of this man’s blood,” Pilate says."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_97_49",
      "id": "fnr_fn_97_49",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("49")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note49"
    }
  }), _vm._v(" This procurator is remembered somewhat differently by the Jewish philosopher Philo of Alexandria, who lived when Pilate did, and wrote sometime around 41 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("C.E.")]), _vm._v(" that the Roman used “bribes, insults, robberies, outrages, wanton injuries, constantly repeated executions without trial, ceaseless and supremely grievous cruelty.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_97_50",
      "id": "fnr_fn_97_50",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("50")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note50"
    }
  }), _vm._v(" Crossan, having cited these words, nevertheless asserts that Pilate “was neither a saint nor a monster.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_97_51",
      "id": "fnr_fn_97_51",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("51")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note51"
    }
  }), _vm._v(" Fredriksen, however, makes the point that Philo, Josephus, and the Roman historian Tacitus all single out Pilate “as one of the worst provocateurs.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_97_52",
      "id": "fnr_fn_97_52",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("52")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note52"
    }
  }), _vm._v(" Even by the standards of brutal Rome, Pilate seems to have been savage. When, six or so years after the death of Jesus, he wantonly slaughtered Samaritans\n"), _c('a', {
    attrs: {
      "id": "p85"
    }
  }), _vm._v(" for gathering to venerate Moses on a sacred mountain they associated with him, Pilate was recalled to Rome."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_98_53",
      "id": "fnr_fn_98_53",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("53")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note53"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("Given the ways in which his occupying force routinely maintained control over a restless population, the Roman commander’s self-exculpation, as recorded in Matthew, in the matter of one particular crucifixion is the moral equivalent of Adolf Eichmann’s standing in his glass booth and declaring himself innocent. “And all the people,” Matthew says, “answered, ‘His blood be on us and on our children!’”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_98_54",
      "id": "fnr_fn_98_54",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("54")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note54"
    }
  }), _vm._v(" Which, of course, it has been.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("This start-to-finish pattern in the Gospels of deflecting blame away from Romans and onto Jews is commonly taken now as evidence of a primordial Christian anti-Judaism, or worse: an anti-Judaism at the service of a craven attempt to placate Roman authorities. But this perception fails to take the “Christian” impulse here as one of people who are in fact Jews. So this anti-Judaism is evidence not of Jew hatred but of the sectarian conflict "), _c('em', [_vm._v("among")]), _vm._v(" Jews. Yes, there may have been an element of attempted ingratiation with Romans, but Jewishness was not the point of distinction in that attempt."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_98_55",
      "id": "fnr_fn_98_55",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("55")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note55"
    }
  }), _vm._v(" As early as 64 "), _c('span', {
    staticClass: "smallcaps"
  }, [_vm._v("C.E.,")]), _vm._v(" well before the Gospels were composed, the emperor Nero had singled out the Jewish sect that claimed Jesus as its "), _c('em', [_vm._v("Christus,")]), _vm._v(" blaming them for the fire that had just then ravaged Rome."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_98_56",
      "id": "fnr_fn_98_56",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("56")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note56"
    }
  }), _vm._v(" Tacitus writes of the violence that Nero inflicted on them,"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_98_57",
      "id": "fnr_fn_98_57",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("57")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note57"
    }
  }), _vm._v(" which is the first recorded mention of the movement. The Christian Jews were labeled as arsonists. They were crucified, burned, and driven out. One of them would flee from Rome to the Aegean island of Patmos to compose the fire-ridden Apocalypse, which labels Rome the beast."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_98_58",
      "id": "fnr_fn_98_58",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("58")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note58"
    }
  }), _vm._v(" The Christian Jews were punished not for what they believed or refused to believe, or for any political threat they posed, but because, as a readily identifiable and vulnerable group, consisting in all likelihood mainly of slaves and lower-class workers with whom other Jews seemed not to identify, they were useful to Nero in providing the angry citizens of Rome with another target for their hatred besides him.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("A. N. Wilson makes the point that Nero’s savage scapegoating of the Christian Jews was for them an organizational boon, giving the until then inconsequential movement a reputation in the empire and numerous martyrs around whom to rally."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_98_59",
      "id": "fnr_fn_98_59",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("59")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note59"
    }
  }), _vm._v(" Two of these, apparently, were Peter and Paul. Long-run organizational boon or not, in the short run Nero’s persecution traumatized the Christian Jews, who knew they had been falsely punished. They knew themselves not to be the violent threat to Roman order that Nero accused them of being. If the Gospels, just then starting to jell in their final forms, emphasized a relative friendliness to Rome, there \n"), _c('a', {
    attrs: {
      "id": "p86"
    }
  }), _vm._v("was a reason for it. The followers of Jesus had just been slandered, defined not merely as Rome’s mortal enemy but as violent insurrectionists. It was not true, and the Gospels were slanted, in effect, to emphasize that followers of Jesus fully intended to render unto Caesar what was Caesar’s. Sectarian tensions between Christians and what Wilson calls the “generality” of Jews may have been exacerbated by the narrow scapegoating, but again, those tensions were multilayered, still decidedly intra-Jewish. But soon enough, after the Gospels had jelled, Rome’s murderous assault on the Jews of Judea would make Nero’s violence seem benign, and explode the boundaries against which Christian-Jewish stresses had begun to press. The trauma of bloodshed on an imperial scale, unprecedented for the Jews, is the necessary context for understanding what was happening in those years among the Jews. Christian anti-Judaism, in others words, is not the first cause here; the Roman war against Judaism is.")]), _vm._v(" "), _c('div', {
    staticClass: "timebreak "
  }), _vm._v(" "), _c('p', {
    staticClass: "left "
  }, [_vm._v("By the Irish analogy, think of the ultimate effect of British imperial power among the Irish themselves. The Irish war with England, begun in 1916, was extremely violent, including as it did the twentieth century’s first indiscriminate shelling of an urban center, Dublin. Part of England’s “draconian reaction”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_99_60",
      "id": "fnr_fn_99_60",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("60")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note60"
    }
  }), _vm._v(" was the unleashing on an unarmed populace of the criminal-terrorist Black and Tans and the post-1918 deployment of trench-veteran tommies, who viewed the Irish war as an extension of the no-holds-barred war against the Hun and fought accordingly. And the first result of all this violence? The Irish population, which in 1916 had been overwhelmingly inclined to favor London—as my great-uncle probably would have—over the self-appointed, self-aggrandizing liberators of the Irish Republican Brotherhood,"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_99_61",
      "id": "fnr_fn_99_61",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("61")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note61"
    }
  }), _vm._v(" by 1920 thought of London as the devil’s own. The fierce, universal Irish hatred of England, a twentieth-century cliché, was in fact born in the twentieth century—just then. Thus even a diehard like Winston Churchill came to recognize that an English victory over this despicable people, short of the outright elimination of the native population, was impossible. Empowered to do so by Eamon de Valera, Michael Collins negotiated the Anglo-Irish Treaty of 1921. There would not be another until 1986.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("There was a second result of the violence of that war. In addition to a unifying Irish hatred of the English, there would be a terribly disunifying Irish hatred of the self. “I tell you this—early this morning I signed my death warrant,” Collins wrote to a friend after agreeing to the treaty, instinctively grasping what awaited him at home."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_99_62",
      "id": "fnr_fn_99_62",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("62")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note62"
    }
  }), _vm._v(" No sooner \n"), _c('a', {
    attrs: {
      "id": "p87"
    }
  }), _vm._v("had the Anglo-Irish war ended than the even more dispiriting Irish civil war began. Forces loyal to de Valera would eventually murder Collins, proving him a prophet. De Valera rejected the central terms of the treaty—an oath of allegiance to the Crown, British hegemony over the six counties in the north—but would later accept them once the paroxysm of Irish self-hatred had run its course. The Irish civil war—unlike, say, the American one—accomplished nothing, except to enable one Irish faction to vent its rage on another. Irish sectarian hatred served the overlord’s purpose well, resulting in an Irish impotence the English could depend on for most of a century. Indeed, Irish sectarian violence was efficiently, if slyly, stoked by London all that time, from Lloyd George’s government to Margaret Thatcher’s.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("Intra-Jewish conflict served Rome’s purposes in just such a way. There is perhaps something craven in the Gospels’ emphasis on “Jews” as a threat to order in the empire, as opposed to “Christians,” and it does not mitigate the Gospel writers’ responsibility for driving this wedge to note that they were responding to Roman oppression. But the more fundamental point is that in doing this, the followers of the murdered Jesus were only demonstrating how effective the imperial overlord had been in infecting the dominated population with its own cynicism and contempt. This dynamic becomes even clearer in the context that has provided us our starting point: One measure of the diabolical efficacy of Nazi torment in Auschwitz, besides the way Jews were victims of SS guards, was the way Jews were victims of fellow Jews, the capos who served as SS surrogates. The collapse of the moral universe that led Jews to participate in their own destruction in the death camps, or to take upon themselves a feeling of guilty responsibility for the evil around them, only emphasizes the abject evil of an absolutely oppressive system. That evil lies in the system’s capacity to destroy the innocence of everyone it touches."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_100_63",
      "id": "fnr_fn_100_63",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("63")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note63"
    }
  }), _vm._v(" When Jewish factions turned Rome’s venom against each other, Rome won yet another victory. There is no question here of “Christian innocence,” because among human beings there is no innocence when the question becomes survival. Extreme violence and extreme measures to survive it form the ground on which this entire story stands.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("It is nevertheless important to emphasize that, well after the life of Jesus, those who remembered the conflicts surrounding both its beginning and its end mainly as conflicts among Jews—Herod’s villainy, not Caesar’s; the high priest’s, not Pilate’s—were being true to the ways these events had come to be understood in the period of heightened \n"), _c('a', {
    attrs: {
      "id": "p88"
    }
  }), _vm._v("Jewish sectarianism that followed Jesus’ death."), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_101_64",
      "id": "fnr_fn_101_64",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("64")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note64"
    }
  }), _vm._v(" Not “innocent,” yet they were not liars either. The Gospel of Matthew was not composed by someone who had been there, not composed by someone who knew well that Pilate was a sadist who’d have thought nothing of dispatching an unknown Galilean troublemaker, and, knowing this, still consciously and falsely portrayed the Romans as innocent and “the Jews” as guilty. It would be a slander to say such a thing of Matthew (or the writers of that Gospel), just as it would slander my mother to say she lied to me when she led me to think her uncle was a hero of the Easter Rising.")]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("Earlier, I cited John Dominic Crossan’s 1995 characterization of the claim that the Jews murdered Jesus as “the longest lie,” but in a subsequent work, in 1998, he amended that judgment. The authors of the foundational Christian documents, writing years after the event,"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_101_65",
      "id": "fnr_fn_101_65",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("65")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note65"
    }
  }), _vm._v(" “did not say this: I know that the Roman authorities crucified Jesus, but I will blame the Jewish authorities; I will play the Roman card; I will write propaganda that I know is inaccurate. If they "), _c('em', [_vm._v("had")]), _vm._v(" done that, the resulting text would have been a lie.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_101_66",
      "id": "fnr_fn_101_66",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("66")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note66"
    }
  }), _vm._v(" Crossan does not attribute such venality to the Gospels, because to do so would impose a post-Enlightenment notion of history on a far more complex phenomenon. Rigid concern for “how it happened” is a contemporary preoccupation of ours, but no such emphasis informed the way the ancients wrote history. Reports of the words and deeds of the late Jesus evolved as his movement grew, and so did the understanding of who his friends and enemies were, depending on the experience through time of who the friends and enemies of the movement were. “As Christian Jewish communities are steadily more alienated from their fellow Jews, so the ‘enemies’ of Jesus expand to fit those new situations. By the time of ‘John’ in the 90s, those enemies are ‘the Jews’—that is, all those other Jews except us few right ones. If we had understood (the literary genre) gospel, we would have understood that. If we had understood gospel, we would have expected that. It is, unfortunately, tragically late to be learning it.”"), _c('span', {
    staticClass: "footnoteref ",
    attrs: {
      "href": "#/back04#fn_101_67",
      "id": "fnr_fn_101_67",
      "title": "Link target outside of Sample"
    }
  }, [_c('sup', [_vm._v("67")])]), _c('a', {
    attrs: {
      "id": "pxchapter9note67"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "follow"
  }, [_vm._v("Just as the original fate of Jesus was shaped in part by intra-Jewish disputes, the communal memory of how that fate unfolded was itself shaped by those disputes, especially when Roman domination of Jews started to unravel. Writing fifty or seventy years after the death of Jesus, the Gospel authors continued to be influenced by the climate of crisis and dispute, Roman terror and Jewish polemic. But around the time of their writing, something new, and for this story something deadly, began to happen.")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-f8e10774","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/c09.vue
// module id = 99
// module chunks = 0